<template>
  <div id="container" :class="{ 'dashboard--pc': isPcRedesign, 'dashboard--mobile': isMobile }">
    <!-- ==================== 페이지 헤더 ==================== -->
    <article class="titleWrap" id="contentTopWrap">
      <div id="contentsGroup">
        <h2>{{ t('menu.dashboard') }}</h2>
      </div>
    </article>

    <!-- ==================== 대시보드 헤더 (커스터마이즈 + 폴링 토글) ==================== -->
    <!-- 기간 필터는 고객사별 접수 현황 위젯의 토글로 통합 (단일 source). -->
    <div class="dashboard-header">
      <div class="dashboard-header__actions">
        <button
          v-if="canCustomize"
          type="button"
          class="dashboard-customize-btn"
          @click="customizePanelOpen = true"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" stroke-width="1.5" fill="none" />
          </svg>
          {{ t('dashboard.customize.openButton') }}
        </button>
        <button
          v-if="!isPcRedesign && !mobileSortMode"
          type="button"
          class="dashboard-customize-btn dashboard-customize-btn--ghost"
          @click="enterMobileSortMode"
        >
          {{ t('dashboard.customize.mobileSortEnter') }}
        </button>
        <button
          v-if="!isPcRedesign && mobileSortMode"
          type="button"
          class="dashboard-customize-btn dashboard-customize-btn--primary"
          @click="exitMobileSortMode"
        >
          {{ t('dashboard.customize.mobileSortDone') }}
        </button>
        <button
          :class="['dashboard-polling-toggle', { 'dashboard-polling-toggle--active': isPolling }]"
          @click="togglePolling"
        >
          <span class="dashboard-polling-toggle__dot"></span>
          {{ isPolling ? t('dashboard.pollingOn') : t('dashboard.pollingOff') }}
        </button>
      </div>
    </div>

    <!-- ==================== 위젯 그리드 (vuedraggable) ==================== -->
    <draggable
      :model-value="visibleWidgets"
      :disabled="!canDrag"
      handle=".widget-drag-handle"
      class="dashboard-widget-grid"
      :class="{ 'dashboard-widget-grid--initial-loading': data.loading.value }"
      item-key="id"
      @update:model-value="reorder"
    >
      <template #item="{ element: widget, index }">
        <div :class="['dashboard-widget-cell', `dashboard-widget-cell--${widget.layout}`]" :data-widget-id="widget.id">
          <DashboardWidgetWrapper
            :widget="widget"
            :title-key="widgetTitleKey(widget)"
            :collapsed="isCollapsed(widget.id)"
            :can-drag="canDrag"
            :mobile-sort-mode="mobileSortMode"
            :is-first="index === 0"
            :is-last="index === visibleWidgets.length - 1"
            @toggle-collapse="toggleCollapse(widget.id)"
            @long-press="enterMobileSortMode"
            @move-up="moveWidget(index, index - 1)"
            @move-down="moveWidget(index, index + 1)"
          >
            <component
              :is="widgetComponentMap[widget.component]"
              v-bind="widgetPropsMap[widget.id]"
              v-on="widgetListenersMap[widget.id] || {}"
            />
          </DashboardWidgetWrapper>
        </div>
      </template>
    </draggable>

    <!-- ==================== ADMIN 풀 커스터마이징 패널 ==================== -->
    <DashboardCustomizePanel
      v-if="canCustomize"
      :show="customizePanelOpen"
      :layout="layout"
      @close="customizePanelOpen = false"
      @apply-preset="applyPreset"
      @reset="resetLayout"
      @toggle-visible="toggleVisible"
      @reorder="reorder"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import draggable from 'vuedraggable'
import { useResponsive } from '@/composables/useResponsive'
import { useDashboardFilter } from '@/composables/useDashboardFilter'
import { useDashboardData } from '@/composables/useDashboardData'
import { useDashboardPolling } from '@/composables/useDashboardPolling'
import { useDashboardLayout } from '@/composables/useDashboardLayout'
import { useDashboardPermission } from '@/composables/useDashboardPermission'
import i18n from '@/locales/i18n'

import DashboardWidgetWrapper from './DashboardWidgetWrapper.vue'
import DashboardCustomizePanel from './DashboardCustomizePanel.vue'

import DashboardHeroPanel from './DashboardHeroPanel.vue'
import CustomerStatusListPanel from './CustomerStatusListPanel.vue'
import RealtimeActivityFeedPanel from './RealtimeActivityFeedPanel.vue'
import ServiceQualityPanel from './ServiceQualityPanel.vue'
import SlaCompliancePanel from './SlaCompliancePanel.vue'
import RegionHeatmap from './RegionHeatmap.vue'
import PartnerKpiTable from './PartnerKpiTable.vue'
import TechnicianCalendarPanel from './TechnicianCalendarPanel.vue'

const t = i18n.global.t
const { isPcRedesign, isMobile } = useResponsive()
const filter = useDashboardFilter()
const data = useDashboardData(filter)
const { isPolling, togglePolling } = useDashboardPolling(data)
const { isAdmin, canCustomize } = useDashboardPermission()
const {
  layout,
  visibleWidgets,
  mobileSortMode,
  isCollapsed,
  toggleCollapse,
  toggleVisible,
  reorder,
  applyPreset,
  resetLayout,
  enterMobileSortMode,
  exitMobileSortMode
} = useDashboardLayout()

const customizePanelOpen = ref(false)

/** 드래그는 PC(>=1280) 전용 — 태블릿/모바일은 1col 레이아웃이라 드래그 무의미 (SURINP-302 확정).
 *  비-PC(<1280)는 정렬 모드 버튼(위/아래 화살표, AC21)로 통일 — 모바일은 long-press, 태블릿은
 *  헤더 '정렬' 버튼으로 진입(SURINP-302: 태블릿=모바일 UI 공유). 버튼 노출 기준은 !isPcRedesign. */
const canDrag = computed(() => isPcRedesign.value)

/** widget.component 문자열 → 실제 Vue 컴포넌트 */
const widgetComponentMap = {
  DashboardHeroPanel,
  CustomerStatusListPanel,
  RealtimeActivityFeedPanel,
  ServiceQualityPanel,
  SlaCompliancePanel,
  RegionHeatmap,
  PartnerKpiTable,
  TechnicianCalendarPanel
}

/** 위젯별 props 매핑 — 각 컴포넌트가 요구하는 prop 형태에 맞게 분기 */
const widgetPropsMap = computed(() => ({
  'today-hero': { summary: data.todaySummary.value, isMobile: isMobile.value },
  'customer-status': {
    items: data.customerStatus.value,
    modelValue: filter.periodStatus.value,
    loading: data.loading.value,
    isMobile: isMobile.value
  },
  'activity-feed': { isMobile: isMobile.value },
  'service-quality': { serviceQuality: data.serviceQuality.value, isPcRedesign: isPcRedesign.value },
  'sla-compliance': { data: data.slaCompliance.value, isPcRedesign: isPcRedesign.value, isMobile: isMobile.value },
  'region-heatmap': { data: data.regionHeatmap.value, isPcRedesign: isPcRedesign.value, isMobile: isMobile.value },
  'partner-kpi': { data: data.partnerKpi.value, isPcRedesign: isPcRedesign.value, isMobile: isMobile.value },
  'technician-calendar': { isMobile: isMobile.value }
}))

/** 위젯별 listener 매핑 — customer-status는 메인 filter와 단일 source 동기화.
 *  data.fetchAll()은 useDashboardData의 periodStatus watcher가 자동 호출 — 이중 호출 금지. */
const widgetListenersMap = computed(() => ({
  'customer-status': {
    'update:modelValue': (period) => {
      filter.changePeriod(period)
    }
  }
}))

/** 위젯별 i18n 타이틀 키 — 기존 dashboard.* string 키 우선 사용 (호환성). */
const WIDGET_TITLE_KEY_MAP = {
  'today-hero': 'dashboard.hero.title',
  'customer-status': 'dashboard.customer.title',
  'activity-feed': 'dashboard.feed.title',
  'service-quality': 'dashboard.qualityTitle',
  'sla-compliance': 'dashboard.slaCompliance',
  'region-heatmap': 'dashboard.regionHeatmap',
  'partner-kpi': 'dashboard.partnerKpi',
  'technician-calendar': 'dashboard.calendar.title'
}

function widgetTitleKey(widget) {
  return WIDGET_TITLE_KEY_MAP[widget.id] || ''
}

/**
 * 모바일 정렬 모드에서 위/아래 화살표 클릭 시 위젯 순서 교환.
 * vuedraggable이 비활성인 상태에서도 reorder를 호출해 LocalStorage에 영속화.
 */
function moveWidget(fromIdx, toIdx) {
  const list = [...visibleWidgets.value]
  if (toIdx < 0 || toIdx >= list.length) return
  const [moved] = list.splice(fromIdx, 1)
  list.splice(toIdx, 0, moved)
  reorder(list)
}

onMounted(() => {
  // 기본 periodStatus = MONTH(useDashboardFilter). 진입 즉시 최근 한달 범위로 fetchAll.
  data.fetchAll()
  if (isAdmin.value && typeof window !== 'undefined' && typeof window.__APP_LOG__ === 'function') {
    window.__APP_LOG__('[Dashboard] Admin mode active — full customize panel available')
  }
})
</script>

<style scoped>
/* ========================================
   AdminDashBoardView — Layout Container
   ======================================== */
.dashboard-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px 0;
  flex-wrap: wrap;
}

.dashboard-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.dashboard-customize-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: var(--radius-full, 9999px);
  border: 1px solid var(--color-gray-200, #B8BCC4);
  background: var(--color-white, #fff);
  color: var(--color-gray-600, #424750);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-fast, 0.15s ease);
}

.dashboard-customize-btn:hover {
  border-color: var(--color-primary, #3B5998);
  color: var(--color-primary, #3B5998);
}

.dashboard-customize-btn svg {
  width: 14px;
  height: 14px;
}

.dashboard-customize-btn--primary {
  background: var(--color-primary, #3B5998);
  border-color: var(--color-primary, #3B5998);
  color: var(--color-white, #fff);
}

.dashboard-customize-btn--primary:hover {
  background: var(--color-primary-dark, #2C4578);
  border-color: var(--color-primary-dark, #2C4578);
  color: var(--color-white, #fff);
}

.dashboard-customize-btn--ghost {
  border-style: dashed;
}

.dashboard-polling-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  border-radius: var(--radius-full, 9999px);
  border: 1px solid var(--color-gray-200, #B8BCC4);
  background: var(--color-white, #fff);
  color: var(--color-gray-500, #5C616B);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-fast, 0.15s ease);
}

.dashboard-polling-toggle--active {
  border-color: var(--color-success, #2E7D5B);
  color: var(--color-success, #2E7D5B);
}

.dashboard-polling-toggle__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-gray-300, #A0A5AE);
  transition: background var(--transition-fast, 0.15s ease);
}

.dashboard-polling-toggle--active .dashboard-polling-toggle__dot {
  background: var(--color-success, #2E7D5B);
  animation: pulse-dot 2s infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

/* ===== Widget Grid ===== */
.dashboard-widget-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-flow: row dense;
  gap: 20px;
  padding-bottom: 40px;
}

/* widget-cell: vuedraggable이 추적하는 native wrapper.
 * grid-column은 명시적 위치(1/2, 2/3) 대신 span 기반으로 — 드래그 중 빈 칸 발생 방지. */
.dashboard-widget-cell {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.dashboard-widget-cell--full { grid-column: span 2; }
.dashboard-widget-cell--half-left,
.dashboard-widget-cell--half-right { grid-column: span 1; }
.dashboard-widget-cell > .dashboard-widget { flex: 1; }

@media (max-width: 1279px) {
  .dashboard-widget-cell--full,
  .dashboard-widget-cell--half-left,
  .dashboard-widget-cell--half-right { grid-column: 1 / -1; }
}

/* 초기 로딩에만 적용 — 폴링/필터 갱신 시에는 깜빡임 방지를 위해 dim 처리 안 함 */
.dashboard-widget-grid--initial-loading {
  opacity: 0.85;
}

/* ===== Tablet (768-1279px) ===== */
@media (min-width: 768px) and (max-width: 1279px) {
  .dashboard-widget-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* ===== Mobile (<768px) ===== */
@media (max-width: 767px) {
  .dashboard-widget-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }
  .dashboard-header {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 12px 0;
  }
  .dashboard-header__actions {
    flex-wrap: wrap;
  }
}
</style>
